import { computed, type Ref } from 'vue'
import type { PlayerTheme } from '../../types/embed'

/**
 * Maps PlayerTheme fields to --vp-* CSS custom properties for inline styling.
 */
export function usePlayerTheme(theme: Ref<PlayerTheme | undefined>) {
  const themeStyle = computed<Record<string, string>>(() => {
    const t = theme.value
    if (!t) return {}

    const vars: Record<string, string> = {}

    if (t.primaryColor) {
      vars['--vp-primary-color'] = t.primaryColor
    }
    if (t.accentColor) {
      vars['--vp-progress-thumb-color'] = t.accentColor
      vars['--vp-volume-thumb-color'] = t.accentColor
    }
    if (t.controlsBgColor) {
      vars['--vp-control-bar-bg'] = t.controlsBgColor
    }
    if (t.controlsTextColor) {
      vars['--vp-control-text-color'] = t.controlsTextColor
      vars['--vp-time-display-color'] = t.controlsTextColor
    }
    // The bar that fills as the media plays, and only that one. The buffered range behind it
    // keeps the base theme's neutral tint: a preset colour on both makes the two indistinguishable
    // and the reader sees a bar already full.
    if (t.progressBarColor) {
      vars['--vp-progress-played-color'] = t.progressBarColor
    }

    return vars
  })

  // `data-vp-theme` knows two palettes; the API's vocabulary has three. `colorful` is the audio
  // player's own and is drawn by that component rather than by these variables, so it reads as
  // `dark` here — which is the narrowing `EmbedPlayer` already applies to `resolvedTheme` before
  // handing it to the video player.
  const effectiveBaseTheme = computed<'dark' | 'light'>(() => {
    const base = theme.value?.baseTheme
    return base === 'light' ? 'light' : 'dark'
  })

  const customCss = computed<string | undefined>(() => {
    return theme.value?.customCss || undefined
  })

  return {
    themeStyle,
    effectiveBaseTheme,
    customCss,
  }
}
